import { useEffect, useMemo, useRef, useState } from 'react'
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode'
import { getLocations, getProducts } from '../../api/master'
import { me as getMe } from '../../api/auth'
import { BackLink, DocHeader } from '../../components/PageParts'
import { getConfig } from '../../api/settings'
import { useToast } from '../../components/Toast.jsx'
import { askPhoneToScan, phoneScans } from '../../utils/phoneScan'
import ErrorBanner from '../../components/ErrorBanner'
import FieldError from '../../components/FieldError'
import Modal from '../../components/Modal'
import Button from '../../components/Button'
import { number as vNumber, min as vMin, runValidation } from '../../utils/validate'
import './pos-modern.css'
import {
  resolveProductByCode,
  completeSale,
  voidSale,
  previewSale,
  listSales,
} from '../../api/sales'
import { docNo } from '../../utils/docNumbers'

function numberOrZero(v) {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}

export default function POS() {
  const { notify } = useToast()
  const NO_LOCATION_ERROR = 'No home location is assigned to this user. Ask an admin to set it in Users.'
  const [cart, setCart] = useState([]) // [{product_id, sku, name, qty, price, line_total}]
  const [code, setCode] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [suggestOpen, setSuggestOpen] = useState(false)
  const [suggestLoading, setSuggestLoading] = useState(false)
  const [qty, setQty] = useState('1')
  const [price, setPrice] = useState('0.00')
  const [discount, setDiscount] = useState('0.00')
  const [payment, setPayment] = useState('Cash')
  const [cashIn, setCashIn] = useState('')
  const [locations, setLocations] = useState([])
  const [locationId, setLocationId] = useState('')
  const [userLocationId, setUserLocationId] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [lastSaleId, setLastSaleId] = useState(null)
  const [lastSaleNo, setLastSaleNo] = useState(null)
  const [busy, setBusy] = useState(false)
  const [keypadOpen, setKeypadOpen] = useState(false)
  const [keypadTarget, setKeypadTarget] = useState('qty') // 'qty' | 'price' | 'cash'
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewData, setPreviewData] = useState(null) // { location_id, products: [...], totals: {...} }
  const [quoteTotals, setQuoteTotals] = useState(null) // lightweight pricing totals from /sales/preview
  const [binTracking, setBinTracking] = useState(false)
  const [posError, setPosError] = useState('')
  const [fieldErrs, setFieldErrs] = useState({})
  const [scannerOpen, setScannerOpen] = useState(false)
  const [scanError, setScanError] = useState('')
  const [autoAddAfterScan, setAutoAddAfterScan] = useState(true)
  const codeRef = useRef(null)
  const scanLockRef = useRef(false)
  const scannerRef = useRef(null)
  const busyRef = useRef(false)
  const suggestTimeoutRef = useRef(null)
  const suppressSuggestRef = useRef(false)
  const scanRegionId = 'pos-scan-region'
  const qtyRef = useRef(null)
  const priceRef = useRef(null)
  const cashRef = useRef(null)
  const currentLocationName = useMemo(() => {
    const loc = locations.find(l => String(l.location_id) === String(locationId))
    return loc ? String(loc.name || `Location ${loc.location_id}`) : ''
  }, [locations, locationId])
  const locationMissing = !locationId

  // Fallback product index for when backend resolver is not available
  const [allProducts, setAllProducts] = useState([])

  useEffect(() => {
    let cancelled = false
    getMe().then((u) => {
      if (cancelled) return
      const roles = (u?.roles || [u?.role]).map(r => String(r || '').toLowerCase()).filter(Boolean)
      const admin = roles.includes('admin')
      setIsAdmin(admin)
      const loc = u?.location_id ? String(u.location_id) : ''
      setUserLocationId(loc)
      if (loc) setLocationId(loc)
      if (!admin && !loc) {
        setPosError(prev => prev || NO_LOCATION_ERROR)
      }
    }).catch(() => { })

    getLocations().then((rows) => {
      if (cancelled) return
      setLocations(rows || [])
    }).catch(() => { })

    // used only for fallback resolve
    getProducts().then(setAllProducts).catch(() => { })
    // feature flags
    getConfig().then(c => setBinTracking(
      c?.bin_tracking === true ||
      c?.bin_tracking === 1 ||
      c?.bin_tracking === '1' ||
      c?.bin_tracking === 'true' ||
      c?.bin_tracking === 'yes' ||
      c?.bin_tracking === 'on'
    )).catch(() => { })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (suppressSuggestRef.current) {
      suppressSuggestRef.current = false
      return
    }
    const q = String(code || '').trim()
    if (!q || q.length < 2 || scannerOpen) {
      setSuggestions([])
      setSuggestOpen(false)
      return
    }
    if (suggestTimeoutRef.current) clearTimeout(suggestTimeoutRef.current)
    suggestTimeoutRef.current = setTimeout(async () => {
      setSuggestLoading(true)
      try {
        const rows = await getProducts({ search: q, limit: 8 })
        setSuggestions(Array.isArray(rows) ? rows : [])
        setSuggestOpen(true)
      } catch {
        setSuggestions([])
        setSuggestOpen(false)
      } finally {
        setSuggestLoading(false)
      }
    }, 250)
    return () => {
      if (suggestTimeoutRef.current) clearTimeout(suggestTimeoutRef.current)
    }
  }, [code, scannerOpen])

  useEffect(() => {
    if (!locations.length) return
    if (!isAdmin && userLocationId) {
      if (String(locationId) !== String(userLocationId)) {
        setLocationId(String(userLocationId))
      }
      return
    }
  }, [locations, isAdmin, userLocationId, locationId])

  useEffect(() => {
    if (isAdmin) {
      if (posError === NO_LOCATION_ERROR) setPosError('')
      return
    }
    if (!userLocationId) {
      setPosError(prev => prev || NO_LOCATION_ERROR)
    } else if (posError === NO_LOCATION_ERROR) {
      setPosError('')
    }
  }, [isAdmin, userLocationId, posError])

  useEffect(() => {
    // Prevent stale tax totals after cart/payment/location edits.
    setPreviewData(null)
    setQuoteTotals(null)
  }, [cart, locationId, payment])

  const total = useMemo(
    () => cart.reduce((sum, r) => sum + numberOrZero(r.line_total), 0),
    [cart]
  )
  const previewTotals = previewData?.totals || quoteTotals || null
  const subtotal = useMemo(
    () => numberOrZero(previewTotals?.subtotal ?? total),
    [previewTotals, total]
  )
  const taxAmount = useMemo(
    () => numberOrZero(previewTotals?.tax_amount ?? 0),
    [previewTotals]
  )
  const grandTotal = useMemo(
    () => numberOrZero(previewTotals?.total ?? (subtotal + taxAmount)),
    [previewTotals, subtotal, taxAmount]
  )
  const taxRate = useMemo(
    () => numberOrZero(previewTotals?.tax_rate ?? 0),
    [previewTotals]
  )
  const taxName = String(previewTotals?.tax_name || 'Sales Tax')

  const change = useMemo(() => {
    if (payment !== 'Cash') return 0
    if (!cart.length || grandTotal <= 0) return 0
    const cash = numberOrZero(cashIn)
    return Math.max(0, cash - grandTotal)
  }, [payment, cashIn, grandTotal, cart.length])

  const charge = useMemo(() => {
    return payment === 'Cash' ? 0 : grandTotal
  }, [payment, grandTotal])

  // Live validation helpers for keypad fields
  const updateQty = (val) => {
    setQty(val)
    let msg = ''
    if (String(val).trim() === '') msg = 'Required'
    else if (isNaN(parseFloat(val))) msg = 'Must be a number'
    else if (parseFloat(val) <= 0) msg = 'Must be > 0'
    setFieldErrs((prev) => ({ ...prev, qty: msg }))
  }
  const updatePrice = (val) => {
    setPrice(val)
    let msg = ''
    if (String(val).trim() !== '') {
      if (isNaN(parseFloat(val))) msg = 'Must be a number'
      else if (parseFloat(val) < 0) msg = 'Must be ≥ 0'
    }
    setFieldErrs((prev) => ({ ...prev, price: msg }))
  }
  const updateCash = (val) => {
    setCashIn(val)
    let msg = ''
    if (payment === 'Cash' && String(val).trim() !== '') {
      if (isNaN(parseFloat(val))) msg = 'Must be a number'
      else if (parseFloat(val) < grandTotal) msg = 'Insufficient cash'
    }
    setFieldErrs((prev) => ({ ...prev, cash: msg }))
  }

  const addLineFromCode = async (overrideCode) => {
    const raw = ((overrideCode ?? code) || '').trim()
    setFieldErrs({})
    if (locationMissing) {
      setPosError(locations.length ? 'Select a location before adding items' : 'No locations configured')
      return
    }
    setPosError('')
    if (!raw) { setPosError('Scan/type a SKU or barcode'); setFieldErrs(prev => ({ ...prev, code: 'Required' })); codeRef.current?.focus(); return }
    if (overrideCode != null) setCode(raw)
    const v = runValidation({ qty, price }, { qty: [vNumber('Qty'), vMin(0.000001, 'Qty')], price: [vNumber('Price'), vMin(0, 'Price')] })
    if (v.qty || v.price) {
      setFieldErrs(prev => ({ ...prev, ...(v.qty ? { qty: v.qty } : {}), ...(v.price ? { price: v.price } : {}) }))
      setPosError('Please fix the highlighted fields')
      if (v.qty) qtyRef.current?.focus(); else if (v.price) priceRef.current?.focus()
      return
    }
    setBusy(true)
    try {
      let meta
      try {
        meta = await resolveProductByCode(raw, locationId)
      } catch (e) {
        // fallback: try to resolve from all products by SKU (exact match)
        const rawLower = raw.toLowerCase()
        const found = allProducts.find(p => String(p.sku) === raw || String(p.barcode || '') === raw)
        if (found) meta = { product_id: found.product_id, sku: found.sku, name: found.name, unit_price: 0 }
        if (!meta) {
          const byName = allProducts.find(p => String(p.name || '').toLowerCase() === rawLower)
          if (byName) meta = { product_id: byName.product_id, sku: byName.sku, name: byName.name, unit_price: 0 }
        }
      }
      if (!meta || !meta.product_id) { setPosError(`No product for code/barcode: ${raw}`); setFieldErrs(prev => ({ ...prev, code: 'Not found' })); return }
      const q = Math.max(0.0001, numberOrZero(qty) || 1)
      const unitPrice = (price && numberOrZero(price) > 0) ? numberOrZero(price) : numberOrZero(meta.unit_price)
      if (unitPrice < 0) { setPosError('Price must be ≥ 0'); setFieldErrs(prev => ({ ...prev, price: 'Must be ≥ 0' })); priceRef.current?.focus(); return }
      const manualDisc = Math.max(0, numberOrZero(discount))
      // Default discount from product settings (percent or amount per unit), scaled by quantity
      let defaultDisc = 0
      const dtype = String(meta.discount_type || '').toLowerCase()
      const dval = numberOrZero(meta.discount_value)
      if (!manualDisc) {
        if (dtype === 'percent') {
          defaultDisc = Math.max(0, Math.min(100, dval)) * unitPrice * q / 100
        } else if (dtype === 'amount') {
          defaultDisc = Math.max(0, Math.min(dval, unitPrice)) * q
        } else if (meta.discount != null) {
          // Fallback if only flat per-unit amount provided
          defaultDisc = Math.max(0, numberOrZero(meta.discount)) * q
        }
      }
      let disc = manualDisc || defaultDisc
      // Clamp to not exceed line extended price
      disc = Math.min(disc, q * unitPrice)
      const lt = Math.round(((q * unitPrice - disc) + Number.EPSILON) * 100) / 100
      setCart(prev => [...prev, {
        product_id: meta.product_id,
        sku: meta.sku,
        name: meta.name,
        qty: q,
        price: unitPrice,
        discount: disc,
        line_total: lt,
      }])
      setPrice((meta.unit_price != null) ? Number(meta.unit_price).toFixed(2) : '0.00')
      setDiscount('0.00')
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  const pickSuggestion = (p) => {
    if (!p) return
    suppressSuggestRef.current = true
    setSuggestOpen(false)
    setSuggestions([])
    setCode(p.sku || '')
    addLineFromCode(p.sku || '')
  }

  const closeSuggestSoon = () => {
    setTimeout(() => setSuggestOpen(false), 120)
  }

  const removeLine = (idx) => {
    setCart(prev => prev.filter((_, i) => i !== idx))
  }

  const clearCart = () => {
    setCart([])
    setCashIn('')
    setFieldErrs((prev) => ({ ...prev, cash: '' }))
  }

  const onLocationChange = (nextValue) => {
    if (!isAdmin) return
    if (String(nextValue) === String(locationId)) return
    if (cart.length) {
      const ok = window.confirm('Changing location will clear the cart. Continue?')
      if (!ok) return
      clearCart()
    }
    setLocationId(nextValue)
    try {
      if (String(nextValue).trim()) localStorage.setItem('posLocationId', String(nextValue))
      else localStorage.removeItem('posLocationId')
    } catch { }
  }

  const saleLines = () => cart.map((r) => ({
    product_id: r.product_id, sku: r.sku, name: r.name,
    qty: numberOrZero(r.qty), price: r.price, discount: numberOrZero(r.discount || 0),
  }))

  useEffect(() => {
    if (!cart.length) {
      setQuoteTotals(null)
      return
    }
    const loc = parseInt(locationId || '0', 10)
    if (!loc) {
      setQuoteTotals(null)
      return
    }
    const timer = setTimeout(async () => {
      try {
        const lines = cart.map((r) => {
          const qty = numberOrZero(r.qty)
          const price = numberOrZero(r.price)
          const discount = numberOrZero(r.discount || 0)
          return {
            product_id: Number(r.product_id),
            qty,
            price,
            discount,
            line_total: Math.max(0, qty * price - discount),
          }
        })
        const prev = await previewSale({ location_id: loc, lines })
        setQuoteTotals(prev?.totals || null)
      } catch {
        // Keep fallback client totals if quote call fails.
      }
    }, 350)
    return () => clearTimeout(timer)
  }, [cart, locationId])

  const doCompleteSale = async () => {
    setPosError('')
    if (!cart.length) { setPosError('Cart is empty'); return }
    const loc = parseInt(locationId || '0', 10)
    if (!loc) { setPosError(locations.length ? 'Please select a Location' : 'No locations configured'); return }
    if (payment === 'Cash') {
      const cash = numberOrZero(cashIn)
      if (cash < grandTotal) { setPosError('Cash received is less than total'); setFieldErrs(prev => ({ ...prev, cash: 'Insufficient cash' })); cashRef.current?.focus(); return }
    }
    const splitLines = saleLines()
    // With bins: where each item comes from (its bins first, then stock here
    // that isn't in a bin yet), and a stop when the location hasn't enough.
    if (binTracking) try {
      const lines = splitLines.map((r) => ({
        product_id: Number(r.product_id),
        qty: numberOrZero(r.qty),
        price: numberOrZero(r.price),
        discount: numberOrZero(r.discount || 0),
      }))
      const prev = await previewSale({ location_id: loc, lines })
      if (prev && prev.products) {
        const short = prev.products.filter((p) => numberOrZero(p.shortage) > 0)
        if (short.length) {
          const list = short.map((p) => `${p.name || p.sku} (need ${numberOrZero(p.need_qty)}, ${numberOrZero(p.bin_available) + numberOrZero(p.not_in_bins)} here)`).join(', ')
          setPosError(`Not enough at ${currentLocationName || 'this location'}: ${list}. Move it here before selling it.`)
          return
        }
        setPreviewData(prev)
        setPreviewOpen(true)
        return // Wait for user action in modal
      }
    } catch (e) {
      // Non-blocking: if preview fails, continue to post
    }

    const payload = {
      payment_method: payment,
      location_id: loc,
      cash_received: payment === 'Cash' ? numberOrZero(cashIn) : undefined,
      lines: splitLines,
    }
    setBusy(true)
    try {
      const res = await completeSale(payload)
      // Clear any previous error banner if the request succeeded
      setPosError('')
      setLastSaleId(res?.sale_id || null)
      setLastSaleNo(res?.sale_no || null)
      const postedTotal = Number(res?.total ?? res?.total_amount ?? grandTotal)
      notify(`${docNo('sale', res?.sale_no) || 'Sale'} completed • ${postedTotal.toFixed(2)}`)
      clearCart()
      setQty('1')
    } catch (e) {
      setPosError(e.message || 'Failed to complete sale')
    } finally {
      setBusy(false)
    }
  }

  const applyKey = (k) => {
    const applyTo = (cur) => {
      if (k === 'C') return ''
      if (k === '⌫') return String(cur || '').slice(0, -1)
      if (k === '.') return String(cur || '').includes('.') ? String(cur || '') : String(cur || '') + '.'
      // default numeric
      return String(cur || '') + String(k)
    }
    if (keypadTarget === 'qty') updateQty(applyTo(qty))
    if (keypadTarget === 'price') updatePrice(applyTo(price))
    if (keypadTarget === 'cash') updateCash(applyTo(cashIn))
  }

  const keypad = (
    <div className="posm-card posm-keypad">
      <div className="posm-keypad-top">
        <div className="posm-keypad-tabs">
          <strong>Keypad</strong>
          <button
            className={`posm-btn posm-btn-outline ${keypadTarget === 'qty' ? 'is-active' : ''}`}
            onClick={() => setKeypadTarget('qty')}
          >
            Qty
          </button>
          <button
            className={`posm-btn posm-btn-outline ${keypadTarget === 'price' ? 'is-active' : ''}`}
            onClick={() => setKeypadTarget('price')}
          >
            Price
          </button>
          {payment === 'Cash' && (
            <button
              className={`posm-btn posm-btn-outline ${keypadTarget === 'cash' ? 'is-active' : ''}`}
              onClick={() => setKeypadTarget('cash')}
            >
              Cash
            </button>
          )}
        </div>
        <button className="posm-btn posm-btn-muted" onClick={() => setKeypadOpen(false)}>Close</button>
      </div>
      <div className="posm-keypad-grid">
        {[7, 8, 9, '⌫', 4, 5, 6, 'C', 1, 2, 3, '↵', '.', 0, '00', '+/-'].map((k, i) => (
          <button
            key={i}
            className="posm-btn posm-btn-outline posm-keypad-key"
            onClick={() => {
              if (k === '↵') return
              if (k === '+/-') return
              applyKey(k)
            }}
          >
            {k}
          </button>
        ))}
      </div>
      <div className="posm-keypad-bottom">
        <button
          className="posm-btn posm-btn-primary"
          onClick={() => addLineFromCode(codeRef.current?.value ?? code)}
          disabled={busy}
        >
          Add Line
        </button>
        <div className="muted">Target: {keypadTarget === 'qty' ? 'Quantity' : keypadTarget === 'cash' ? 'Cash' : 'Price'}</div>
      </div>
    </div>
  )

  const promptVoid = async () => {
    const txt = window.prompt('Enter the sale number to void (e.g. SALE-0045):')
    if (!txt) return
    const no = parseInt(String(txt).replace(/\D/g, ''), 10)
    if (!no) { setPosError('Enter a sale number such as SALE-0045.'); return }
    const sale = ((await listSales({ saleNo: no }).catch(() => [])) || []).find((r) => !r.original_sale_id)
    if (!sale) { setPosError(`There is no sale ${docNo('sale', no)}.`); return }
    await doVoid(sale.sale_id, no)
  }

  const doVoid = async (sid, no) => {
    const label = docNo('sale', no) || 'this sale'
    if (!window.confirm(`Void ${label}? This reverses the sale and puts the stock back.`)) return
    try {
      await voidSale(sid)
      notify(`${label} voided`)
      if (lastSaleId === sid) { setLastSaleId(null); setLastSaleNo(null) }
    } catch (e) {
      setPosError(e.message || 'Failed to void sale')
    }
  }

  const stopScanner = async () => {
    const inst = scannerRef.current
    if (!inst) return
    try { await inst.stop() } catch { }
    try { await inst.clear() } catch { }
    scannerRef.current = null
  }

  useEffect(() => {
    busyRef.current = busy
  }, [busy])

  useEffect(() => {
    const onKey = (e) => {
      if (busyRef.current || scannerOpen) return
      const tag = (e.target && e.target.tagName) ? e.target.tagName.toLowerCase() : ''
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 's') {
        e.preventDefault()
        setScannerOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [scannerOpen])

  useEffect(() => {
    if (!scannerOpen) return;

    // Inside the phone app the phone's own scanner reads it. The box isn't
    // focused afterwards there, so the keyboard doesn't cover the sale.
    if (phoneScans()) {
      return askPhoneToScan(async (cleaned) => {
        setScannerOpen(false);
        if (!cleaned) return;
        suppressSuggestRef.current = true;
        setCode(cleaned);
        if (autoAddAfterScan) await addLineFromCode(cleaned);
      });
    }

    scanLockRef.current = false;
    setScanError('');

    const scanner = new Html5Qrcode(scanRegionId);
    scannerRef.current = scanner;

    // Bigger box on iPhone + better for 1D barcodes (wide rectangle)
    const qrbox = (vw, vh) => {
      const minEdge = Math.min(vw, vh);
      // iPhone needs more pixels on 1D bars; go larger than 260
      const width = Math.min(340, Math.floor(minEdge * 0.92));
      const height = Math.floor(width * 0.55); // wide barcode rectangle
      return { width, height };
    };

    const config = {
      fps: 10,
      qrbox,
      aspectRatio: 1.7777778,
      disableFlip: true,
      experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      formatsToSupport: [
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.CODE_39,
        Html5QrcodeSupportedFormats.ITF,      // ✅ cartons/labels
        Html5QrcodeSupportedFormats.CODABAR,  // ✅ some retail labels
      ],
    };

    // Confirm gate: require the same code twice quickly (more stable on iPhone)
    let last = { text: "", ts: 0 };
    const CONFIRM_MS = 450;

    (async () => {
      try {
        // Prefer explicit back camera id (more reliable than facingMode on iOS)
        const cams = await Html5Qrcode.getCameras();
        const back =
          cams.find(c => /back|rear|environment/i.test(c.label)) ||
          cams[cams.length - 1];

        await scanner.start(
          back?.id ? { deviceId: { exact: back.id } } : { facingMode: "environment" },
          config,
          async (decodedText) => {
            if (scanLockRef.current || busyRef.current) return;

            const cleaned = String(decodedText || "").trim();
            if (!cleaned) return;

            const now = Date.now();
            if (cleaned !== last.text || (now - last.ts) > CONFIRM_MS) {
              last = { text: cleaned, ts: now };
              return; // wait for confirm read
            }

            scanLockRef.current = true;
            await stopScanner();
            setScannerOpen(false);

            suppressSuggestRef.current = true;
            setCode(cleaned);
            if (autoAddAfterScan) await addLineFromCode(cleaned);
            codeRef.current?.focus();
          },
          () => { }
        );
      } catch (e) {
        console.error(e);
        setScanError('Camera blocked or unavailable—type barcode or use Bluetooth scanner.');
      }
    })();

    return () => { stopScanner(); };
  }, [scannerOpen, autoAddAfterScan]);

  return (
    <div className="posm-root">
      <DocHeader
        back={<BackLink to="/sales" label="Sales" />}
        title="New sale"
        meta={currentLocationName ? `Selling from ${currentLocationName}` : 'Choose your location at the bottom before you complete the sale.'}
      />
      {posError && <ErrorBanner message={posError} />}

      <section className="posm-card posm-scan-card">
        <div className="posm-scan-grid">
          <div className="posm-search-col">
            <div className="posm-search-group">
              <div className="posm-search-wrap">
                <input
                  className="posm-input"
                  placeholder="Scan/type SKU, barcode, or product name"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addLineFromCode() }}
                  onFocus={() => { if (suggestions.length) setSuggestOpen(true) }}
                  onBlur={closeSuggestSoon}
                  ref={codeRef}
                  disabled={busy}
                />
                {suggestOpen && (suggestLoading || suggestions.length) && (
                  <div className="pos-suggest">
                    {suggestLoading && <div className="pos-suggest-item muted">Searching…</div>}
                    {!suggestLoading && suggestions.map((p) => (
                      <button
                        key={p.product_id}
                        type="button"
                        className="pos-suggest-item"
                        onMouseDown={(e) => { e.preventDefault() }}
                        onClick={() => pickSuggestion(p)}
                      >
                        <div className="pos-suggest-title">{p.name}</div>
                        <div className="pos-suggest-meta">{p.sku}{p.barcode ? ` • ${p.barcode}` : ''}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button className="posm-btn posm-btn-primary" type="button" onClick={() => setScannerOpen(true)} disabled={busy}>
                Scan
              </button>
            </div>
            <FieldError error={fieldErrs.code} />
            <label className="posm-checkline">
              <input type="checkbox" checked={autoAddAfterScan} onChange={e => setAutoAddAfterScan(e.target.checked)} />
              Auto-add after scan
            </label>
          </div>

          <div className="posm-quick-grid">
            <div className="posm-field">
              <label className="posm-label">Qty</label>
              <input
                ref={qtyRef}
                className="posm-input posm-input-right"
                value={qty}
                onChange={e => updateQty(e.target.value)}
                onFocus={() => { if (keypadOpen) setKeypadTarget('qty') }}
              />
              <FieldError error={fieldErrs.qty} />
            </div>
            <div className="posm-field">
              <label className="posm-label">Price</label>
              <input
                ref={priceRef}
                className="posm-input posm-input-right"
                value={price}
                onChange={e => updatePrice(e.target.value)}
                onFocus={() => { if (keypadOpen) setKeypadTarget('price') }}
              />
              <FieldError error={fieldErrs.price} />
            </div>
            <div className="posm-field">
              <label className="posm-label">Discount</label>
              <input
                className="posm-input posm-input-right"
                value={discount}
                onChange={e => setDiscount(e.target.value)}
              />
            </div>
          </div>

          <div className="posm-action-col">
            <button
              className="posm-btn posm-btn-primary"
              onClick={() => addLineFromCode(codeRef.current?.value ?? code)}
              disabled={busy || locationMissing}
            >
              Add Line
            </button>
            <button className="posm-btn posm-btn-outline" onClick={() => setKeypadOpen(v => !v)}>
              {keypadOpen ? 'Hide Keypad' : 'Show Keypad'}
            </button>
          </div>
        </div>
        {keypadOpen && keypad}
      </section>

      {scannerOpen && !phoneScans() && (
        <div className="scan-modal" onClick={() => { setScannerOpen(false); stopScanner() }}>
          <div className="scan-card" onClick={(e) => e.stopPropagation()}>
            <div className="scan-head">
              <div style={{ fontWeight: 700 }}>Scan Barcode</div>
              <button className="btn" type="button" onClick={() => { setScannerOpen(false); stopScanner() }}>Close</button>
            </div>
            <div className="scan-body">
              <div id={scanRegionId} className="scan-region" />
              <div className="muted" style={{ marginTop: 8 }}>Point camera at barcode</div>
              {scanError && <div className="muted" style={{ marginTop: 6 }}>{scanError}</div>}
              <label className="scan-toggle" style={{ marginTop: 10 }}>
                <input type="checkbox" checked={autoAddAfterScan} onChange={e => setAutoAddAfterScan(e.target.checked)} />
                Auto-add after scan
              </label>
            </div>
          </div>
        </div>
      )}

      <section className="posm-card posm-table-card">
        <table className="posm-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Name</th>
              <th className="posm-num">Qty</th>
              <th className="posm-num">Price</th>
              <th className="posm-num">Discount</th>
              <th className="posm-num">Line Total</th>
              <th className="posm-center">Remove</th>
            </tr>
          </thead>
          <tbody>
            {cart.length === 0 && (
              <tr>
                <td colSpan={7} className="posm-empty">No items yet. Scan a product to begin.</td>
              </tr>
            )}
            {cart.map((r, i) => (
              <tr key={i}>
                <td><strong>{r.sku}</strong></td>
                <td>{r.name}</td>
                <td className="posm-num">{r.qty}</td>
                <td className="posm-num">{Number(r.price).toFixed(2)}</td>
                <td className="posm-num">{Number(r.discount || 0).toFixed(2)}</td>
                <td className="posm-num"><strong>{Number(r.line_total).toFixed(2)}</strong></td>
                <td className="posm-center">
                  <button className="posm-btn posm-btn-soft" onClick={() => removeLine(i)}>✕ Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="posm-card posm-checkout-card">
        <div className="posm-subtotal-box">
          <div className="posm-subtotal-row">
            <span>Subtotal</span>
            <strong>{subtotal.toFixed(2)}</strong>
          </div>
          <div className="posm-subtotal-row">
            <span>{taxName} ({taxRate.toFixed(3)}%)</span>
            <strong>{taxAmount.toFixed(2)}</strong>
          </div>
          <div className="posm-total-row">
            <span>Total</span>
            <strong>{grandTotal.toFixed(2)}</strong>
          </div>
        </div>

        <div className="posm-totals-grid">
          <div className="posm-total-card">
            <div className="posm-total-label">Charge</div>
            <div className="posm-total-value">{charge.toFixed(2)}</div>
          </div>
          <div className="posm-total-card">
            <div className="posm-total-label">Change Due</div>
            <div className="posm-total-value">{change.toFixed(2)}</div>
          </div>
        </div>

        <div className="posm-payment-grid">
          <div className="posm-field">
            <label className="posm-label">Payment</label>
            <select className="posm-select" value={payment} onChange={e => setPayment(e.target.value)}>
              <option>Cash</option>
              <option>Card</option>
              <option>AR</option>
            </select>
          </div>

          {payment === 'Cash' && (
            <>
              <div className="posm-field">
                <label className="posm-label">Cash Received</label>
                <input
                  className="posm-input"
                  placeholder="0.00"
                  value={cashIn}
                  onChange={e => updateCash(e.target.value)}
                  onFocus={() => { if (keypadOpen) setKeypadTarget('cash') }}
                  ref={cashRef}
                />
                <FieldError error={fieldErrs.cash} />
              </div>
              <button className="posm-btn posm-btn-outline" onClick={() => setCashIn(grandTotal.toFixed(2))}>Exact Cash</button>
              <button className="posm-btn posm-btn-outline" onClick={() => setCashIn(Math.round(grandTotal).toFixed(2))}>Round to Nearest</button>
            </>
          )}

          <div className="posm-field">
            <label className="posm-label">Location</label>
            <select className="posm-select" value={locationId} onChange={e => onLocationChange(e.target.value)} disabled={!isAdmin}>
              <option value="">{locations.length ? 'Select location' : 'No locations'}</option>
              {locations.map(l => (
                <option key={l.location_id} value={l.location_id}>{l.location_id}: {l.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="posm-final-row">
          <button className="posm-btn posm-btn-outline-danger" onClick={promptVoid}>Void a sale…</button>
          <div className="posm-final-actions">
            <button className="posm-btn posm-btn-muted" onClick={clearCart}>Clear Cart</button>
            <button className="posm-btn posm-btn-success" onClick={doCompleteSale} disabled={busy}>
              {busy ? 'Working…' : 'Complete Sale'}
            </button>
            <button className="posm-btn posm-btn-danger" onClick={() => lastSaleId && doVoid(lastSaleId, lastSaleNo)} disabled={!lastSaleId}>
              Void Last
            </button>
          </div>
        </div>
      </section>

      {/* Sale Preview Modal */}
      <PreviewModal
        open={previewOpen}
        data={previewData}
        onCancel={() => setPreviewOpen(false)}
        onProceed={async () => {
          const loc = parseInt(locationId || '0', 10)
          const splitLines = saleLines()
          const payload = {
            payment_method: payment,
            location_id: loc,
            cash_received: payment === 'Cash' ? numberOrZero(cashIn) : undefined,
            lines: splitLines,
          }
          setPreviewOpen(false)
          setBusy(true)
          try {
            const res = await completeSale(payload)
            setPosError('')
            setLastSaleId(res?.sale_id || null)
            setLastSaleNo(res?.sale_no || null)
            const postedTotal = Number(res?.total ?? res?.total_amount ?? grandTotal)
            notify(`${docNo('sale', res?.sale_no) || 'Sale'} completed • ${postedTotal.toFixed(2)}`)
            clearCart()
            setQty('1')
          } catch (e) {
            setPosError(e.message || 'Failed to complete sale')
          } finally {
            setBusy(false)
          }
        }}
      />
    </div>
  )
}

function PreviewModal({ open, data, onCancel, onProceed }) {
  const products = data?.products || []
  return (
    <Modal
      open={open && !!data}
      onClose={onCancel}
      title={`Sale Preview — Location ${data?.location_id ?? ''}`}
      size="lg"
      footer={
        <>
          <div className="muted" style={{ marginRight: 'auto' }}>Total shortage: {Number(data?.totals?.shortage_sum || 0).toFixed(2)}</div>
          <div className="muted">Subtotal: {Number(data?.totals?.subtotal || 0).toFixed(2)}</div>
          <div className="muted">{String(data?.totals?.tax_name || 'Sales Tax')}: {Number(data?.totals?.tax_amount || 0).toFixed(2)}</div>
          <div style={{ fontWeight: 700 }}>Total: {Number(data?.totals?.total || 0).toFixed(2)}</div>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={onProceed}>Proceed</Button>
        </>
      }
    >
      <div className="muted" style={{ marginBottom: 8 }}>Where each item comes from: its bins first, then stock here that isn’t in a bin yet.</div>
      <table className="table table-sm">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Name</th>
            <th style={{ textAlign: 'right' }}>Need</th>
            <th style={{ textAlign: 'right' }}>Bin Avail</th>
            <th style={{ textAlign: 'right' }}>Not in a bin</th>
            <th style={{ textAlign: 'right' }}>Shortage</th>
            <th style={{ textAlign: 'right' }}>Global</th>
            <th>Allocations</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, i) => (
            <tr key={i}>
              <td>{p.sku || p.product_id}</td>
              <td>{p.name}</td>
              <td style={{ textAlign: 'right' }}>{Number(p.need_qty).toFixed(2)}</td>
              <td style={{ textAlign: 'right' }}>{Number(p.bin_available).toFixed(2)}</td>
              <td style={{ textAlign: 'right' }}>{Number(p.not_in_bins || 0).toFixed(2)}</td>
              <td style={{ textAlign: 'right', color: Number(p.shortage) > 0 ? '#b91c1c' : undefined }}>{Number(p.shortage).toFixed(2)}</td>
              <td style={{ textAlign: 'right' }}>{Number(p.current_global).toFixed(2)} → {Number(p.projected_global).toFixed(2)}</td>
              <td>{(p.allocations || []).map(a => `${a.path} (${Number(a.take).toFixed(2)})`).join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  )
}
